import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Edit, Refresh, Lock, Add, Email } from '@mui/icons-material';
import { gamoraApi, User, UserUpdateRequest } from '../services/api';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [userDetailsDialogOpen, setUserDetailsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<UserUpdateRequest>({});
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userSubscriptions, setUserSubscriptions] = useState<any[]>([]);
  const [userPayments, setUserPayments] = useState<any[]>([]);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await gamoraApi.getAllUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      username: user.username,
      email: user.email,
      role: user.role,
      company: user.company,
      tokens_available: user.tokens_available,
      tokens_purchased: user.tokens_purchased,
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      setError('');
      setSuccess('');
      await gamoraApi.updateUser(selectedUser.id, editForm);
      setSuccess('User updated successfully!');
      setEditDialogOpen(false);
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handlePasswordUpdate = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordDialogOpen(true);
  };

  const handleQuickAddTokens = async (user: User, amount: number) => {
    try {
      setError('');
      setSuccess('');
      await gamoraApi.updateUser(user.id, {
        tokens_available: user.tokens_available + amount,
        tokens_purchased: user.tokens_purchased + amount,
      });
      setSuccess(`Added ${amount} tokens to ${user.username}`);
      fetchUsers(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add tokens');
    }
  };

  const handleViewUserDetails = async (user: User) => {
    setSelectedUser(user);
    setLoadingUserDetails(true);
    setUserDetailsDialogOpen(true);
    
    try {
      // Fetch user subscriptions and payments
      const [subscriptions, payments] = await Promise.all([
        gamoraApi.getUserSubscriptions(user.id),
        gamoraApi.getUserPayments(user.id)
      ]);
      
      setUserSubscriptions(subscriptions || []);
      setUserPayments(payments || []);
    } catch (err: any) {
      console.error('Error fetching user details:', err);
      setUserSubscriptions([]);
      setUserPayments([]);
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleSendPaymentEmail = async (subscription: any) => {
    if (!selectedUser) return;
    
    setSendingEmail(subscription.id);
    try {
      setError('');
      setSuccess('');
      
      await gamoraApi.sendPaymentEmail({
        user_id: selectedUser.id,
        subscription_id: subscription.id,
        amount: subscription.amount / 100,
        currency: subscription.currency,
        plan_name: subscription.plan_name
      });
      
      setSuccess(`Payment success email sent to ${selectedUser.email}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(null);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!selectedUser) return;

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setError('');
      setSuccess('');
      await gamoraApi.updateUser(selectedUser.id, { password: newPassword });
      setSuccess('Password updated successfully!');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update password');
    }
  };

  const getRoleLabel = (role: number) => {
    switch (role) {
      case 0: return 'Super Admin';
      case 10: return 'Enterprise';
      case 20: return 'Regular';
      default: return 'Unknown';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontSize: '0.85rem', color: '#9ca3af' }}>Loading users...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography component="h2" sx={{ fontSize: '1.1rem', fontWeight: 650, color: '#111827', letterSpacing: '-0.02em' }}>
          User Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchUsers}
          sx={{
            borderColor: '#e5e7eb',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.78rem',
            color: '#6b7280',
            '&:hover': { borderColor: '#d1d5db', backgroundColor: '#f8f9fb' }
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Token Statistics */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ 
          position: 'relative',
          p: 2, 
          bgcolor: '#fff', 
          border: '1px solid #eceef1',
          borderRadius: '12px',
          minWidth: 150,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            bgcolor: '#6366f1'
          }
        }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Users</Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>{users.length}</Typography>
        </Box>
        <Box sx={{ 
          position: 'relative',
          p: 2, 
          bgcolor: '#fff', 
          border: '1px solid #eceef1',
          borderRadius: '12px',
          minWidth: 150,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            bgcolor: '#22c55e'
          }
        }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Tokens Available</Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>
            {users.reduce((sum, user) => sum + user.tokens_available, 0).toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ 
          position: 'relative',
          p: 2, 
          bgcolor: '#fff', 
          border: '1px solid #eceef1',
          borderRadius: '12px',
          minWidth: 150,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            bgcolor: '#3b82f6'
          }
        }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Tokens Purchased</Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>
            {users.reduce((sum, user) => sum + user.tokens_purchased, 0).toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ 
          position: 'relative',
          p: 2, 
          bgcolor: '#fff', 
          border: '1px solid #eceef1',
          borderRadius: '12px',
          minWidth: 150,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            bgcolor: '#f59e0b'
          }
        }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Avg Tokens/User</Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>
            {users.length > 0 ? Math.round(users.reduce((sum, user) => sum + user.tokens_available, 0) / users.length).toLocaleString() : 0}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>
          {success}
        </Alert>
      )}

      <TableContainer 
        component={Paper}
        sx={{
          maxHeight: 'calc(100vh - 400px)',
          overflow: 'auto',
          bgcolor: '#fff',
          border: '1px solid #eceef1',
          borderRadius: '12px',
          boxShadow: 'none'
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>ID</TableCell>
              <TableCell sx={{ bgcolor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Username</TableCell>
              <TableCell sx={{ bgcolor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Email</TableCell>
              <TableCell sx={{ bgcolor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Role</TableCell>
              <TableCell sx={{ bgcolor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Company</TableCell>
              <TableCell sx={{ bgcolor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tokens Available</TableCell>
              <TableCell sx={{ bgcolor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tokens Purchased</TableCell>
              <TableCell sx={{ bgcolor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Last Token Purchase</TableCell>
              <TableCell sx={{ bgcolor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Created</TableCell>
              <TableCell sx={{ bgcolor: '#f8f9fb', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow 
                key={user.id}
                onClick={() => handleViewUserDetails(user)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f8f9fb'
                  }
                }}
              >
                <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{user.id}</TableCell>
                <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{user.username}</TableCell>
                <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{user.email}</TableCell>
                <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{getRoleLabel(user.role)}</TableCell>
                <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{user.company || '-'}</TableCell>
                <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 'bold',
                      color: user.tokens_available > 1000 ? '#22c55e' : user.tokens_available > 100 ? '#f59e0b' : '#ef4444'
                    }}>
                      {user.tokens_available.toLocaleString()}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>
                  <Typography variant="body2">
                    {user.tokens_purchased.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>
                  <Typography variant="body2">
                    {user.last_token_purchase ? formatDate(user.last_token_purchase) : 'Never'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>{formatDate(user.created_at)}</TableCell>
                <TableCell sx={{ fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f3f4f6' }} onClick={(e) => e.stopPropagation()}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Edit User">
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                        sx={{
                          color: '#9ca3af',
                          width: 30,
                          height: 30,
                          '&:hover': { color: '#111827' }
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Update Password">
                      <IconButton
                        size="small"
                        onClick={() => handlePasswordUpdate(user)}
                        sx={{
                          color: '#9ca3af',
                          width: 30,
                          height: 30,
                          '&:hover': { color: '#111827' }
                        }}
                      >
                        <Lock />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Add 1000 Tokens">
                      <IconButton
                        size="small"
                        onClick={() => handleQuickAddTokens(user, 1000)}
                        sx={{
                          color: '#9ca3af',
                          width: 30,
                          height: 30,
                          '&:hover': { color: '#111827' }
                        }}
                      >
                        <Add />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit User Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)' } }}
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 650, color: '#111827' }}>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Username"
              value={editForm.username || ''}
              onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
              margin="normal"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8f9fb' } }}
            />
            <TextField
              fullWidth
              label="Email"
              value={editForm.email || ''}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              margin="normal"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8f9fb' } }}
            />
            <TextField
              fullWidth
              label="Company"
              value={editForm.company || ''}
              onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
              margin="normal"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8f9fb' } }}
            />
            <FormControl fullWidth margin="normal" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8f9fb' } }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={editForm.role || 20}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value as number })}
                label="Role"
              >
                <MenuItem value={0}>Super Admin</MenuItem>
                <MenuItem value={10}>Enterprise</MenuItem>
                <MenuItem value={20}>Regular</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              type="password"
              label="New Password (leave blank to keep current)"
              value={editForm.password || ''}
              onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
              margin="normal"
              helperText="Only fill this if you want to change the password"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8f9fb' } }}
            />
            <TextField
              fullWidth
              type="number"
              label="Tokens Available"
              value={editForm.tokens_available || 0}
              onChange={(e) => setEditForm({ ...editForm, tokens_available: parseInt(e.target.value) || 0 })}
              margin="normal"
              helperText="Current available tokens for the user"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8f9fb' } }}
            />
            <TextField
              fullWidth
              type="number"
              label="Tokens Purchased"
              value={editForm.tokens_purchased || 0}
              onChange={(e) => setEditForm({ ...editForm, tokens_purchased: parseInt(e.target.value) || 0 })}
              margin="normal"
              helperText="Total tokens purchased by the user"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8f9fb' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: '#6b7280', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleUpdateUser} variant="contained" sx={{ bgcolor: '#111827', borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#1f2937' } }}>
            Update User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Update Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={() => setPasswordDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)' } }}
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 650, color: '#111827' }}>Update Password for {selectedUser?.username}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              helperText="Password must be at least 6 characters long"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8f9fb' } }}
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8f9fb' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)} sx={{ color: '#6b7280', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            disabled={!newPassword || !confirmPassword || newPassword.length < 6}
            sx={{ bgcolor: '#111827', borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#1f2937' } }}
          >
            Update Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog 
        open={userDetailsDialogOpen} 
        onClose={() => setUserDetailsDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)' } }}
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 650, color: '#111827' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 650, color: '#111827' }}>
              User Details: {selectedUser?.username}
            </Typography>
            <Button onClick={() => setUserDetailsDialogOpen(false)} sx={{ color: '#6b7280', textTransform: 'none' }}>
              Close
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingUserDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography sx={{ fontSize: '0.85rem', color: '#9ca3af' }}>Loading user details...</Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              {/* User Information */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 650, color: '#111827' }}>
                  User Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>Username</Typography>
                    <Typography variant="body1" sx={{ color: '#374151' }}>{selectedUser?.username}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>Email</Typography>
                    <Typography variant="body1" sx={{ color: '#374151' }}>{selectedUser?.email}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>Role</Typography>
                    <Typography variant="body1" sx={{ color: '#374151' }}>{getRoleLabel(selectedUser?.role || 0)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>Company</Typography>
                    <Typography variant="body1" sx={{ color: '#374151' }}>{selectedUser?.company || 'None'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>Tokens Available</Typography>
                    <Typography variant="body1" sx={{ 
                      fontWeight: 'bold',
                      color: (selectedUser?.tokens_available || 0) > 1000 ? '#22c55e' : 
                             (selectedUser?.tokens_available || 0) > 100 ? '#f59e0b' : '#ef4444'
                    }}>
                      {(selectedUser?.tokens_available || 0).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>Tokens Purchased</Typography>
                    <Typography variant="body1" sx={{ color: '#374151' }}>{(selectedUser?.tokens_purchased || 0).toLocaleString()}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>Last Token Purchase</Typography>
                    <Typography variant="body1" sx={{ color: '#374151' }}>
                      {selectedUser?.last_token_purchase ? formatDate(selectedUser.last_token_purchase) : 'Never'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#9ca3af' }}>Created</Typography>
                    <Typography variant="body1" sx={{ color: '#374151' }}>{formatDate(selectedUser?.created_at || '')}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Subscriptions */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 650, color: '#111827' }}>
                  Subscriptions ({userSubscriptions.length})
                </Typography>
                {userSubscriptions.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Plan</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Tokens/Month</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userSubscriptions.map((sub) => (
                          <TableRow key={sub.id}>
                            <TableCell>{sub.plan_name}</TableCell>
                            <TableCell>
                              <Box sx={{ 
                                display: 'inline-block',
                                px: 1,
                                py: 0.5,
                                borderRadius: '6px',
                                fontSize: '0.72rem',
                                bgcolor: sub.status === 'ACTIVE' ? 'rgba(34,197,94,0.15)' : 
                                        sub.status === 'PENDING' ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
                                color: sub.status === 'ACTIVE' ? '#22c55e' : 
                                       sub.status === 'PENDING' ? '#f59e0b' : '#ef4444'
                              }}>
                                {sub.status}
                              </Box>
                            </TableCell>
                            <TableCell>{sub.currency} {(sub.amount / 100).toFixed(2)}</TableCell>
                            <TableCell>{sub.tokens_per_month.toLocaleString()}</TableCell>
                            <TableCell>{formatDate(sub.created_at)}</TableCell>
                            <TableCell>
                              <Tooltip title="Send payment success email to user">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => handleSendPaymentEmail(sub)}
                                  disabled={sendingEmail === sub.id}
                                  startIcon={sendingEmail === sub.id ? <CircularProgress size={16} /> : <Email />}
                                >
                                  {sendingEmail === sub.id ? 'Sending...' : 'Send Email'}
                                </Button>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography sx={{ color: '#9ca3af' }}>No subscriptions found</Typography>
                )}
              </Box>

              {/* Payments */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 650, color: '#111827' }}>
                  Payment History ({userPayments.length})
                </Typography>
                {userPayments.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Payment ID</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Tokens Added</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userPayments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {payment.dodo_payment_id}
                              </Typography>
                            </TableCell>
                            <TableCell>{payment.currency} {(payment.amount / 100).toFixed(2)}</TableCell>
                            <TableCell>
                              <Box sx={{ 
                                display: 'inline-block',
                                px: 1,
                                py: 0.5,
                                borderRadius: '6px',
                                fontSize: '0.72rem',
                                bgcolor: payment.status === 'COMPLETED' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                color: payment.status === 'COMPLETED' ? '#22c55e' : '#ef4444'
                              }}>
                                {payment.status}
                              </Box>
                            </TableCell>
                            <TableCell>{payment.tokens_added?.toLocaleString() || 0}</TableCell>
                            <TableCell>{formatDate(payment.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography sx={{ color: '#9ca3af' }}>No payment history found</Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UserManagement; 